import React from 'react';
import { Sparkles } from 'lucide-react';
import { useSecondScreenData } from '../../../contexts/SecondScreenContext';
import type { WidgetProps } from '../../../types/second-screen.types';

const SpotlightWidget: React.FC<WidgetProps> = () => {
  const { state, theme, activeSpotlight } = useSecondScreenData();
  const isDark = theme === 'dark';
  const sessionAvatars = state?.sessionAvatars ?? [];

  if (!activeSpotlight) {
    return (
      <div className={`flex flex-col items-center justify-center h-full gap-2 ${
        isDark ? 'text-white/20' : 'text-slate-300'
      }`}>
        <Sparkles size={24} />
        <span className="text-xs">Waiting for spotlight...</span>
      </div>
    );
  }

  const avatar = sessionAvatars.find(a => a.role === activeSpotlight.role);
  const urgencyColors: Record<string, string> = {
    urgent: 'bg-red-500/20 text-red-400 border-red-500/30',
    important: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    normal: 'bg-primary/20 text-primary border-primary/30',
  };

  return (
    <div className="flex flex-col h-full p-4 gap-3">
      {/* Avatar header */}
      <div className="flex items-center gap-3 shrink-0">
        {avatar?.imageUrl ? (
          <img
            src={avatar.imageUrl}
            alt={avatar.name}
            className="w-12 h-12 rounded-full border-2 border-primary/50 object-cover"
          />
        ) : (
          <div
            className="w-12 h-12 rounded-full border-2 border-primary/50 flex items-center justify-center text-xl font-bold"
            style={{ backgroundColor: avatar?.primaryColor || '#475569', color: '#fff' }}
          >
            {avatar?.emoji || avatar?.name?.charAt(0) || '?'}
          </div>
        )}
        <div className="min-w-0">
          <h3 className={`text-sm font-bold truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {avatar?.name || activeSpotlight.name}
          </h3>
          <div className="flex items-center gap-2 mt-0.5">
            <span className={`text-xs capitalize ${isDark ? 'text-white/40' : 'text-slate-500'}`}>
              {activeSpotlight.role.replace(/_/g, ' ')}
            </span>
            <span className={`text-xs font-bold uppercase px-1.5 py-0.5 rounded border ${
              urgencyColors[activeSpotlight.urgency] || urgencyColors.normal
            }`}>
              {activeSpotlight.urgency}
            </span>
          </div>
        </div>

        {/* Animated sparkle indicator */}
        <div className="ml-auto">
          <Sparkles size={16} className="text-primary animate-pulse" />
        </div>
      </div>

      {/* Contribution text */}
      <div className={`flex-1 min-h-0 overflow-y-auto rounded-lg p-3 border ${
        isDark ? 'bg-white/[0.03] border-white/5' : 'bg-slate-50 border-slate-100'
      }`}>
        <p className={`text-xs leading-relaxed ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
          {activeSpotlight.response}
        </p>
      </div>
    </div>
  );
};

export default SpotlightWidget;
